import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TrxPriceData {
  price: number;
  change24h: number;
  marketCap: number;
  volume24h: number;
  totalSupply: number;
  circulatingSupply: number;
  lastUpdated: string;
}

const CACHE_KEY = "trx_price_cache";
const CACHE_DURATION = 25 * 60 * 1000; // 1 minute cache

export const useTrxPrice = () => {
  const [data, setData] = useState<TrxPriceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPrice = useCallback(async (forceRefresh = false) => {
    try {
      // Check cache first (only for initial load, not for refresh)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setData(cachedData);
            setIsLoading(false);
            // Still fetch fresh data in background
            fetchFreshData();
            return;
          }
        }
      }

      await fetchFreshData();
    } catch (err) {
      console.error("Error fetching TRX price:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch price");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFreshData = async () => {
    try {
      const { data: responseData, error: fetchError } = await supabase.functions.invoke("coinmarketcap");

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (responseData.error) {
        throw new Error(responseData.error);
      }

      setData(responseData);
      setError(null);

      // Cache the result
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: responseData,
          timestamp: Date.now(),
        }),
      );
    } catch (err) {
      console.error("Error fetching fresh TRX price:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch price");
    }
  };

  useEffect(() => {
    fetchPrice();

    // Refresh every 2 minutes
    const interval = setInterval(fetchPrice, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPrice]);

  return { data, isLoading, error, refetch: fetchPrice };
};
